import React, { useEffect, useState } from 'react';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import { useForm } from 'react-hook-form';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { ESearchUserFormLabelsTypes } from 'constants/SearchFormLabelsTypes';
import { initialUserSearchFormValues, ISearchUserFormValues } from 'models/form';
import { useRoles } from 'pages/RolesPage/hooks';
import { EPerPages } from 'models/table';
import { prepareFilters } from 'helpers/filters';
import { useTranslations } from 'hooks/useTranslations';
import { IRole } from 'models/role';
import { StyledSearchForm } from 'theme/styles';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';

enum ESearchFormLabels {
  email = 'users.email.label',
  firstName = 'users.firstName.label',
  lastName = 'users.lastName.label',
  phoneNumber = 'users.phoneNumber.label',
  status = 'users.status.label',
  rolesUniqueIds = 'users.role.label',
  displayUniqueId = 'users.displayUniqueId.label',
}

interface IUserSearchForm {
  onSubmit: (arg: ISearchUserFormValues) => void;
  initialSearchForm: ISearchUserFormValues;
  isSearchButtonDisabled?: boolean;
}

export enum EUserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  BLOCKED = 'BLOCKED',
  REMOVED = 'REMOVED',
}

const UserSearchForm = ({
  onSubmit,
  initialSearchForm,
  isSearchButtonDisabled = false,
}: IUserSearchForm) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm<ISearchUserFormValues>({
    defaultValues: initialSearchForm,
  });
  const [values, setValues] = useState<ISearchUserFormValues>(initialSearchForm);
  const [roles, setRoles] = useState<IRole[]>([]);
  const { getRolesData } = useRoles();
  const { t } = useTranslations();
  const statusData = [
    { label: t('general.active.label'), value: EUserStatus.ACTIVE },
    { label: t('general.inactive.label'), value: EUserStatus.INACTIVE },
    { label: t('users.blocked.label'), value: EUserStatus.BLOCKED },
    { label: t('users.removed.label'), value: EUserStatus.REMOVED },
  ];

  useEffect(
    () => {
      (async function getRolesDataInit() {
        const rolesData = await getRolesData(1, EPerPages.perPage50);
        if (rolesData) {
          setRoles(rolesData.content);
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as ISearchUserFormValues);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    setValues(initialSearchForm);
    reset(initialSearchForm);
    handleSubmit(onSubmit)();
  }, [initialSearchForm]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name) {
      reset({ ...getValues(), [name]: '' });
    } else {
      reset(initialUserSearchFormValues);
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  return (
    <SearchWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledSearchForm container spacing={2} columns={10} sx={{ mb: { xs: 2, md: 0 } }}>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="displayUniqueId"
              label={t(ESearchFormLabels.displayUniqueId)}
              control={control}
              setValue={setValue}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="email"
              label={t(ESearchFormLabels.email)}
              control={control}
              setValue={setValue}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="firstName"
              label={t(ESearchFormLabels.firstName)}
              control={control}
              setValue={setValue}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="lastName"
              label={t(ESearchFormLabels.lastName)}
              control={control}
              setValue={setValue}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="phoneNumber"
              label={t(ESearchFormLabels.phoneNumber)}
              control={control}
              setValue={setValue}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <FormMultiSelect
              options={roles.map((role) => ({
                label: role.name,
                value: role.uniqueId,
              }))}
              name="rolesUniqueIds"
              setValue={setValue}
              canBeEmpty={true}
              label={t(ESearchFormLabels.rolesUniqueIds)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <FormMultiSelect
              options={statusData}
              name="statuses"
              setValue={setValue}
              canBeEmpty={true}
              label={t(ESearchFormLabels.status)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 6 }}>
            <Grid container spacing={2} sx={{ justifyContent: 'flex-end' }}>
              <Grid size={{ xs: 12, lg: 4 }}>
                <Button
                  id="users_search-button"
                  variant={EButtonVariants.contained}
                  size={EButtonSizes.small}
                  disabled={isSearchButtonDisabled}
                  label={t('users.search.button')}
                  type="submit"
                  fullWidth
                  sx={{ mt: 3.5 }}
                />
              </Grid>
            </Grid>
          </Grid>
        </StyledSearchForm>
      </form>
      <SelectedFilters
        name="user"
        values={values}
        excludes={['removed']}
        labels={ESearchUserFormLabelsTypes}
        handleDelete={handleResetField}
        dictionaries={{
          statuses: prepareFilters(statusData, 'value', 'label'),
          rolesUniqueIds: prepareFilters(roles, 'uniqueId', 'name'),
        }}
        noPreset
        showSaveFilters={false}
        saveRouterStateAfterClearAll={false}
      />
    </SearchWrapper>
  );
};

export { UserSearchForm };
